module Sm.Model.RecordSet

open System
open MobF

open Sm.Core.Timer

open Sm.Service.Repository
open Sm.Service.Storage

open Sm.Model.Record
open Sm.Model.RealtimeQuery

type RecordQuery =
    | Journal of DateOnly
    | Pending

module RecordQuery =
    let eval id readMeta query = 
        let (|RecordId|_|) = Id.parse

        match query with
        | Journal date ->
            match id with
            | RecordId (c, t) -> c = JournalCode && date = t.Date
            | _ -> false
        | Pending ->
            match id, readMeta Document.ArchivedField with
            | RecordId (c, _), None -> c = JournalCode
            | _ -> false

type State = {
    Query: RealtimeQuery.Type<RecordQuery, Map<Guid, Record.Type>>
    Version: int
}

type Msg =
    | NextVersion
    | Reset
    | Load of RecordQuery
    | Insert of Source

type IComputed =
    abstract Ready: Result<bool, string>
    abstract Index: Map<Guid, Record.Type>
    abstract Records: Record.Type seq

type Type = Model<State, Msg, IComputed>

type Options = {
    IncludeArchived: bool
    FlushDebounce: int
    OnCreate: Record.Type -> unit
}

type Options with
    static member Default =
        {
            IncludeArchived = true
            FlushDebounce = 1000
            OnCreate = fun _ -> ()
        }

let create (repository: Repository) options =
    let pending = ResizeArray<Update>()
    let mutable flushed = Set.empty

    let scheduleFlush = debounce options.FlushDebounce (fun () ->
        promise {
            let updates = Array.ofSeq pending

            flushed <- Set.ofSeq pending
            pending.Clear()

            do! repository.Merge(updates)
        }
        |> Promise.start
    )

    let dispatch adjustment =
        let update = repository.Prepare([adjustment])
        pending.AddRange(update)
        scheduleFlush ()

    let init post =
        let query = RealtimeQuery.create {
            Empty = Map.empty
            Repository = repository
            Execute = (fun query -> promise {
                let! docs = 
                    match query with
                    | Journal date -> repository.QueryJournal(date)
                    | Pending -> repository.QueryPendingEntries()

                return docs
                |> Seq.choose fromStorageContent
                |> Seq.map (fun source ->
                    let record = Record.create source dispatch
                    options.OnCreate record
                    record.State.Id, record
                )
                |> Map.ofSeq
            })
            Merge = (fun query e records ->
                let accept =
                    let readMeta key = e.Fields.TryFind(key) |> Option.bind _.Value
                    query |> RecordQuery.eval e.Id readMeta

                let update = e.Update
                let target = update.Target

                if flushed |> Set.contains update then
                    flushed <- flushed.Remove(update)
                    records
                elif accept then
                    match records.TryFind(e.Id) with
                    | Some record ->
                        record.Post(
                            match target.Value with
                            | None -> HandleMetadataCleared target.Field
                            | (Some v) when target.Field = Document.TextField -> HandleTextChanged v
                            | Some v -> HandleMetadataChanged (target.Field, v)
                        )
                        records
                    | None ->
                        match fromStorageFields e.Id e.Fields with
                        | None -> records
                        | Some source ->
                            let record = Record.create source dispatch
                            options.OnCreate record
                            records |> Map.add record.State.Id record
                else
                    records |> Map.remove e.Id
            )
        }

        query |> Subscribe.auto
            (fun x -> x.Operation)
            (function
                | Ready _ -> post NextVersion
                | _ -> ()
            )

        {
            Query = query
            Version = 0
        }

    let update state = function
        | NextVersion ->
            { state with Version = state.Version + 1 }

        | Reset ->
            state.Query.Post(Update Unknown)
            state

        | Load query ->
            state.Query.Post(Execute query)
            state

        | Insert source ->
            match state.Query.State.Operation with
            | Ready (q, Ok records) ->
                let record = Record.create source dispatch
                options.OnCreate record

                let merge =
                    let readMeta key = source.Metadata.TryFind(key)
                    q |> RecordQuery.eval source.Id readMeta

                if merge then
                    let nextRecords = records |> Map.add record.State.Id record
                    let nextOp = Ready (q, Ok nextRecords)

                    state.Query.Post(Update nextOp)

                seq {
                    { Id = source.Id; Field = Document.TextField; Value = Some source.Text }

                    for KeyValue(name, value) in source.Metadata do
                        { Id = source.Id; Field = name; Value = Some value }
                }
                |> repository.Prepare
                |> pending.AddRange

                scheduleFlush ()
            | _ -> ()
            state

    let compute state =
        { new IComputed with
            member _.Ready = state.Query.Computed.Ready
            member _.Index = state.Query.Computed.Result
            member this.Records = this.Index.Values
        }

    Model.useInitWithPost init
    |> Model.andUpdate update
    |> Model.createWithComputed compute
